import React, { useCallback, useEffect, useState } from 'react';
import { Download, Info, Loader2, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CollarSelect, SectionSelect } from '@/components/staffAttendance/SectionSelect';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { money } from '@/components/staffAttendance/PayrollAgencyCard';
import { PayrollReviewList } from '@/components/staffAttendance/PayrollReviewList';
import { RegularPayrollTable } from '@/components/staffAttendance/RegularPayrollTable';
import { matchStaff, SearchNote } from '@/components/staffAttendance/StaffSearch';
import { staffAttApi, errMsg, todayIST } from '@/services/staffAttendanceApi';
import { downloadCsv } from '@/services/transportApi';

const STATS = [['staff', 'Staff'], ['fixed_staff', 'Fixed'], ['variable_staff', 'Variable'], ['not_set', 'Pay type not set'], ['worked_days', 'Worked days'],
  ['days', 'Fixed days'], ['night_hours', 'Night hrs (Fixed)'], ['review', 'HR to decide'], ['missed', 'Missed check-outs'], ['pending', 'Waiting approval']];
const WARN = new Set(['missed', 'review', 'not_set']);
const PAY = [['', 'All pay types'], ['fixed', 'Fixed'], ['variable', 'Variable'], ['none', 'Pay type not set']];
const MAX_DAYS = 62;
const monthStart = () => `${todayIST().slice(0, 8)}01`;
const span = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 864e5) + 1;
const rangeError = (a, b) => (!a || !b ? 'Pick both From and To dates' : a > b ? 'From date must be on or before To date'
  : span(a, b) > MAX_DAYS ? `Pick a range of ${MAX_DAYS} days or less (this one is ${span(a, b)} days)` : '');

const Totals = ({ t }) => (
  <div className="space-y-3" data-testid="regpay-totals">
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {STATS.map(([k, l]) => (
        <div key={k} className="rounded-xl bg-white shadow-sm p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-500">{l}</div>
          <div className={`text-2xl font-bold tabular-nums ${WARN.has(k) && t[k] ? 'text-amber-600' : ''}`} data-testid={`regpay-total-${k}`}>{t[k]}</div>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {[['fixed_amount', 'Fixed pay', 'bg-amber-50 text-amber-900'], ['variable_amount', 'Variable pay', 'bg-teal-50 text-teal-900'], ['amount', 'Total payroll', 'bg-[#1A1F2E] text-white']].map(([k, l, c]) => (
        <div key={k} className={`rounded-xl shadow-sm p-3 ${c}`}>
          <div className="text-[11px] uppercase tracking-wide opacity-70">{l}</div>
          <div className="text-2xl font-bold tabular-nums" data-testid={`regpay-total-${k}`}>{money(t[k])}</div>
        </div>
      ))}
    </div>
  </div>
);

/** Regular (non-contract) staff pay for a date range: Fixed pay type = Fixed ÷ 26 × days + night hrs; Variable = piece-rate + supervisor. */
export const RegularPayroll = ({ sections, search = '', canEdit = false }) => {
  const [f, setF] = useState({ date_from: monthStart(), date_to: todayIST(), section: '', collar: '', pay_type: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const err = rangeError(f.date_from, f.date_to);
  const load = useCallback(() => {
    if (err) return;
    setLoading(true);
    staffAttApi.regularPayroll(params).then(setData).catch((e) => toast.error(errMsg(e, 'Failed to load payroll'))).finally(() => setLoading(false));
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const csv = () => downloadCsv(`/staff-attendance/regular-payroll?${new URLSearchParams({ ...params, format: 'csv' })}`, `regular_payroll_${f.date_from}_to_${f.date_to}.csv`)
    .catch((e) => toast.error(errMsg(e, 'Export failed')));
  const t = data?.totals;
  const rows = (data?.staff || []).filter((r) => matchStaff(search, r.staff_name, r.staff_id));
  const reviews = (data?.reviews || []).filter((r) => matchStaff(search, r.staff_name, r.staff_id));
  return (
    <div className="space-y-4" data-testid="regular-payroll">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="w-[160px] bg-white" data-testid="regpay-from" />
        <span className="text-sm text-gray-500">to</span>
        <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="w-[160px] bg-white" data-testid="regpay-to" />
        <SectionSelect sections={sections} value={f.section} onChange={(v) => setF({ ...f, section: v })} testId="regpay-section" />
        <CollarSelect value={f.collar} onChange={(v) => setF({ ...f, collar: v })} testId="regpay-collar-select" />
        <select value={f.pay_type} onChange={(e) => setF({ ...f, pay_type: e.target.value })} className="h-10 rounded-md border border-gray-300 bg-white px-3 text-sm" data-testid="regpay-paytype-filter">
          {PAY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Button variant="outline" onClick={csv} disabled={!!err} data-testid="regpay-csv-btn"><Download className="w-4 h-4 mr-1" />Download CSV</Button>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-amber-600" />}
      </div>
      {err ? <p className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700" data-testid="regpay-range-error">{err}</p> : <>
        {data && <p className="text-sm text-gray-600" data-testid="regpay-period">Showing <b>{data.period}</b> · {data.days.length} day{data.days.length === 1 ? '' : 's'} · Night rate (Fixed) <b>{money(data.night_rate)} / hr</b></p>}
        {data?.rules && (
          <div className="flex items-start gap-2 rounded-lg bg-white border border-gray-200 px-3 py-2 text-xs text-gray-600" data-testid="regpay-rules">
            <Info className="w-4 h-4 mt-0.5 text-[#2D5F4F] shrink-0" />{data.rules}
          </div>
        )}
        {t?.collar_split && <CollarTiles split={t.collar_split} value={f.collar} onPick={(v) => setF({ ...f, collar: v })} metric="Payroll"
          fmt={money} sub={(s) => `${s.staff} staff`} testId="regpay-collar-tiles" />}
        {t && <Totals t={t} />}
        {t?.not_paid_variable > 0 && (
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" data-testid="regpay-notpaid-note">
            <TriangleAlert className="w-4 h-4" />Fixed staff also earned {money(t.not_paid_variable)} piece-rate / supervisor pay — not paid (Fixed pay only).
          </div>
        )}
        <PayrollReviewList reviews={reviews} canDecide={canEdit && !!data?.can_decide} onChanged={load} decide={staffAttApi.setRegularPayDecision} groupLabel="Section" />
        {data && data.staff.length > 0 && <SearchNote q={search} shown={rows.length} total={data.staff.length} testId="regpay-search-note" />}
        {!data ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-8 text-amber-600" /> : data.staff.length === 0
          ? <div className="rounded-xl bg-white shadow-sm py-12 text-center text-gray-500" data-testid="regpay-empty">No regular staff pay in this date range</div>
          : <RegularPayrollTable key={JSON.stringify(params) + search} rows={rows} />}
      </>}
    </div>
  );
};

export default RegularPayroll;
